<template>
    <!-- native modifier has been removed, please confirm whether the function has been affected  -->
    <BtnCta
        data-testid="btn-cta-promise"
        v-bind="$attrs"
        :variant="state !== BtnCtaPromiseState.DEFAULT ? 'secondary' : 'primary'"
        class="whitespace-nowrap transition-all ease-in-out duration-200"
        :color="state === BtnCtaPromiseState.SUCCESS ? 'positive' : $attrs.color"
        :icon="state === BtnCtaPromiseState.SUCCESS ? 'checkmark-stroke' : $attrs.icon"
        :label="buttonLabel"
        :loading="state === BtnCtaPromiseState.PROGRESS"
        :disabled="isClickable && disabled"
        @click="onClick"
    >
        <template
            v-for="(_, slot) of $slots"
            #[slot]
        >
            <slot :name="slot" />
        </template>
    </BtnCta>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue';
import { getPromiseState } from '@/helpers/promise';
import { type EnumToUnion } from '@/types/misc';
import { BtnCtaPromiseState } from './BtnCtaPromise';

/**
 * This damn piece of a button can watch for a promise action and display progress & success states.
 * @emits progress
 * @emits reset
 * @emits success
 * @emits error
 */
export default defineComponent({
    name: 'BtnCtaPromise',
    inheritAttrs: false,
    props: {
        /**
         * Default label text.
         */
        label: {
            type: String,
            required: true,
        },
        /**
         * Label text shown in the succes state.
         */
        successLabel: {
            type: String,
            required: true,
        },
        /**
         * Function that should be executed on click.
         * The resulting promise controls the state of the button.
         */
        action: {
            type: Function as PropType<() => Promise<boolean>>,
            required: true,
        },
        /**
         * Whether to disable the button.
         * Note: Clicks in the progress & success phase will not trigger the action.
         */
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * Duation in milliseconds to reset the button after the action promise is fulfilled.
         */
        delayReset: {
            type: Number,
            default: 3000,
        },
    },
    emits: [
        'reset',
        'progress',
        'success',
        'error',
    ],
    setup() {
        return {
            BtnCtaPromiseState,
        };
    },
    data: () => ({
        state: BtnCtaPromiseState.DEFAULT as EnumToUnion<BtnCtaPromiseState>,
    }),
    computed: {
        isClickable(): boolean {
            return this.state === BtnCtaPromiseState.DEFAULT;
        },
        buttonLabel(): string {
            switch (this.state) {
                case BtnCtaPromiseState.SUCCESS:
                    return this.successLabel;
                default:
                    return this.label;
            }
        },
    },
    methods: {
        async onClick() {
            if (!this.isClickable) return;

            const promise = this.action();

            if (!(promise instanceof Promise)) return;

            // we only want to show progress if the promise is not yet resolved
            if ((await getPromiseState(promise)) === 'pending') {
                this.setState(BtnCtaPromiseState.PROGRESS);
            }

            promise
                .then((val) => {
                    if (val) {
                        this.setState(BtnCtaPromiseState.SUCCESS, val);
                    } else {
                        this.onError();
                    }
                })
                .catch(this.onError);
        },
        setState(state: EnumToUnion<BtnCtaPromiseState>, data: unknown = undefined) {
            this.state = state;

            switch (state) {
                case BtnCtaPromiseState.DEFAULT: {
                    /**
                     * Emitted when the button is being reset to its initial state.
                     */
                    this.$emit('reset', data);
                    break;
                }
                case BtnCtaPromiseState.PROGRESS: {
                    /**
                     * Emitted when button state is switched into the progress phase.
                     */
                    this.$emit('progress', data);
                    break;
                }
                case BtnCtaPromiseState.SUCCESS: {
                    /**
                     * Emitted when the button state is switched into the success phase.
                     */
                    this.$emit('success', data);
                    setTimeout(() => {
                        this.setState(BtnCtaPromiseState.DEFAULT);
                    }, this.delayReset);
                    break;
                }
                default:
                    break;
            }
        },
        onError(e: unknown = undefined) {
            /**
             * Emitted when the action resolves to `false` or throws an error.
             * @property {unknown} e the thrown error
             */
            this.$emit('error', e);
            this.setState(BtnCtaPromiseState.DEFAULT);

            // we will re-throw it in case someone is interested
            // in catching this error up the chain
            if (e instanceof Error) throw e;
        },
    },
});
</script>
